import { useEffect, useRef, useState } from 'react';
import { findRowKeyPath } from '../utils/accountStructure/accountSearch';

const HIGHLIGHT_MS = 2600;

export const LOCATE_HIT_CLASS = 'account-locate-hit';

// Requests a table has already acted on. Shared across tables so one mounted
// later (e.g. when the user changes tab) doesn't replay an old request.
const handledRequests = new WeakSet();

/**
 * Reacts to a page-level `focusRequest` ({ identity }) by expanding the
 * ancestors of the matching row, scrolling it into view and returning its key
 * so the table can flash it. Tables that don't contain the account ignore it.
 *
 * @returns {{ highlightKey: string|null }}
 */
export function useLocateRow({ focusRequest, rows, getKey, containerRef, onExpand, onFound }) {
    const [highlightKey, setHighlightKey] = useState(null);
    const timers = useRef([]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    // Re-checked when rows change, so a table that was still loading when the
    // request came in handles it once its rows arrive.
    useEffect(() => {
        if (!focusRequest || handledRequests.has(focusRequest)) return;

        const path = findRowKeyPath(rows, focusRequest.identity, getKey);
        if (!path) return;
        handledRequests.add(focusRequest);

        const key = path[path.length - 1];
        onExpand?.(path.slice(0, -1));
        onFound?.(key);
        setHighlightKey(key);

        timers.current.forEach(clearTimeout);
        timers.current = [
            // Wait for the expanded rows to render before scrolling.
            setTimeout(() => {
                const root = containerRef?.current ?? document;
                const row = root.querySelector?.(`tr[data-row-key="${CSS.escape(String(key))}"]`);
                row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 80),
            setTimeout(() => setHighlightKey(null), HIGHLIGHT_MS),
        ];
        // getKey/onExpand/onFound are recreated every render; they don't signal a new request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusRequest, rows]);

    return { highlightKey };
}
